import {
  CFormCheck,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import DuplicateIcon from "../../components/icons/DuplicIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { Chip, isPositive } from "../../helpers/general";
import { useEventQuery } from "../../hooks/events/useEventQuery";
import { useAuth } from "../../hooks/useAuth";
import { iconBtnStyle, tdStyle, thStyle } from "../../helpers/constants";

/** Round icon link to a sub-page (services, reservations...) */
function OpenLink({ to, title }: { to: string; title: string }) {
  return (
    <Link
      to={to}
      title={title}
      aria-label={title}
      className="d-inline-flex align-items-center justify-content-center rounded-circle"
      style={iconBtnStyle}
    >
      <ArrowTopRightOnSquareIcon width="16" height="16" color="#555" />
    </Link>
  );
}

function IconLink({
  to,
  state,
  title,
  children,
}: {
  to: string;
  state?: any;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      state={state}
      title={title}
      aria-label={title}
      className="d-inline-flex align-items-center justify-content-center rounded-circle"
      style={iconBtnStyle}
    >
      {children}
    </Link>
  );
}

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  event,
  deleteList,
}: {
  event: Events;
  deleteList: React.Dispatch<BulkDeleteActions>;
}) {
  const [showWarning] = useState<boolean>(() => {
    if (
      !event?.paymentOptions?.includes("stripe") ||
      (event?.services ?? []).length === 0
    ) {
      return false;
    }
    return (
      event.services?.some(
        (service) => !isPositive(service.startingPriceEuro),
      ) ?? false
    );
  });
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { isAdmin } = useAuth();

  const { removeEvent } = useEventQuery();

  useEffect(() => {
    if (isChecked) {
      deleteList({ type: DELETE_ACTIONS.ADD, id: event?.id });
    } else {
      deleteList({ type: DELETE_ACTIONS.REMOVE, id: event?.id });
    }
  }, [deleteList, isChecked, event?.id]);

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Event */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 260 }}
        >
          <img
            src={
              event?.image ??
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                event?.name ?? "",
              )}`
            }
            alt=""
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ borderRadius: 10, objectFit: "cover" }}
          />
          <div style={{ minWidth: 0 }}>
            <Link
              to={`/events/${event?.id}`}
              className="fw-semibold text-capitalize d-block text-truncate"
              style={{ maxWidth: 320 }}
              title={event?.name}
            >
              {event?.name}
            </Link>
            <div
              className="text-muted text-truncate"
              style={{ fontSize: 12, maxWidth: 320 }}
              title={event?.address}
            >
              {event?.address}
            </div>
            {showWarning && (
              <div className="mt-1">
                <Chip tone="danger">
                  ⚠ Certains services n&apos;ont pas de prix en euros
                </Chip>
              </div>
            )}
          </div>
        </div>
      </CTableDataCell>

      {/* Phone */}
      <CTableDataCell style={tdStyle}>
        {event?.phone ? (
          <span style={{ whiteSpace: "nowrap" }}>{event.phone}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Page visits */}
      <CTableDataCell style={tdStyle}>
        <Chip>{event?.pageVisits ?? 0} visites</Chip>
      </CTableDataCell>

      {/* Services */}
      <CTableDataCell style={tdStyle}>
        <OpenLink to={`/events/${event?.id}/services`} title="Services" />
      </CTableDataCell>

      {/* Reservations */}
      <CTableDataCell style={tdStyle}>
        <OpenLink
          to={`/events/${event?.id}/reservation`}
          title="Réservations"
        />
      </CTableDataCell>

      {/* Promo codes */}
      <CTableDataCell style={tdStyle}>
        <OpenLink to={`/promo-codes/${event?.id}/event`} title="Codes promo" />
      </CTableDataCell>

      {/* Statistics */}
      <CTableDataCell style={tdStyle}>
        {Number.isFinite(event?.venezPercentage) ? (
          <OpenLink
            to={`/events/${event?.id}/statistics`}
            title="Statistiques"
          />
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconLink to={`/events/update/${event.id}`} title="Modifier">
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconLink>
          <IconLink to={`/events/duplicate/${event.id}`} title="Dupliquer">
            <DuplicateIcon width="16" height="16" fill={colors.secondary} />
          </IconLink>
          {isAdmin && (
            <IconLink
              to="/event-translations/new"
              state={{ event }}
              title="Traduire"
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </IconLink>
          )}
          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            <DeleteIcon width="18" height="18" fill={colors.primary} />
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        onClose={() => setShowModal(false)}
        onAccept={() => removeEvent(event?.id)}
        open={showModal}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  events: Events[];
  eventsToBeDeletedList: React.Dispatch<BulkDeleteActions>;
}

export default function EventsTable({
  eventsToBeDeletedList,
  events,
}: TableProps) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              Nom
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Téléphone
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Visites de page
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Services
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Réservations
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Codes promo
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Statistiques
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle} />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {events?.map((event) => (
            <TableBodyRowContent
              key={event?.id}
              event={event}
              deleteList={eventsToBeDeletedList}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
